/**
 * Small pieces the Logistics screens share.
 *
 * Kept here rather than in the global UI kit because each one encodes something
 * about this domain — a stat tile that says "—" rather than "₦0" when a figure
 * does not apply, and a two-direction row that refuses to collapse what CenoHub
 * owes with what the rider owes.
 */

import type { ReactNode } from 'react';
import { cn } from '../../components/ui';
import { money, moneyCompact } from '../../lib/format';

/**
 * One figure.
 *
 * `value === null` renders an em dash, not a zero. Remittance has no daily
 * figure — it is agreed per period — and showing ₦0 would be a claim that
 * nothing is owed rather than that the question does not apply.
 */
export function Stat({
  label,
  value,
  currency,
  hint,
  tone = 'plain',
  count,
}: {
  label: string;
  value?: string | null;
  currency?: string;
  hint?: string;
  tone?: 'plain' | 'good' | 'warn' | 'owed';
  count?: number;
}): ReactNode {
  const display =
    count !== undefined
      ? new Intl.NumberFormat('en-US').format(count)
      : value === null || value === undefined
        ? '—'
        : moneyCompact(value, currency ?? 'NGN');

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={cn(
          'mt-1 text-lg font-semibold tabular-nums',
          tone === 'good' && 'text-emerald-700',
          tone === 'warn' && 'text-amber-700',
          tone === 'owed' && 'text-rose-700',
          tone === 'plain' && 'text-slate-900',
        )}
        // The exact figure, for anyone reconciling against a statement.
        title={value !== null && value !== undefined && currency ? money(value, currency) : undefined}
      >
        {display}
      </p>
      {hint ? <p className="mt-0.5 text-xs text-slate-400">{hint}</p> : null}
    </div>
  );
}

/** A labelled row inside a card, for statement-style detail. */
export function Line({
  label,
  value,
  strong,
  tone = 'plain',
  note,
}: {
  label: string;
  value: ReactNode;
  strong?: boolean;
  tone?: 'plain' | 'good' | 'owed';
  note?: string;
}): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className={cn('text-sm', strong ? 'font-medium text-slate-900' : 'text-slate-600')}>
        {label}
        {note ? <span className="ml-1.5 text-xs text-slate-400">{note}</span> : null}
      </span>
      <span
        className={cn(
          'text-sm tabular-nums',
          strong ? 'font-semibold' : 'font-medium',
          tone === 'good' && 'text-emerald-700',
          tone === 'owed' && 'text-rose-700',
          tone === 'plain' && 'text-slate-900',
        )}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * The two directions of a settlement, side by side.
 *
 * Deliberately two columns and never one signed number. "We owe him ₦3,500"
 * and "he owes us ₦1,000" are different facts about different people, and a
 * single figure of ₦2,500 tells a dispatcher neither of them.
 */
export function TwoWay({
  owedToRider,
  owedToCenohub,
  currency,
}: {
  owedToRider: string;
  owedToCenohub: string;
  currency: string;
}): ReactNode {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2.5">
        <p className="text-xs font-medium text-emerald-800">CenoHub owes rider</p>
        <p className="mt-0.5 text-base font-semibold tabular-nums text-emerald-900">
          {money(owedToRider, currency)}
        </p>
      </div>
      <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-2.5">
        <p className="text-xs font-medium text-rose-800">Rider owes CenoHub</p>
        <p className="mt-0.5 text-base font-semibold tabular-nums text-rose-900">
          {money(owedToCenohub, currency)}
        </p>
      </div>
    </div>
  );
}

/** Section heading inside a screen. */
export function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }): ReactNode {
  return (
    <div className="mb-2.5 flex items-center justify-between gap-3">
      <h2 className="text-sm font-semibold text-slate-900">{children}</h2>
      {aside}
    </div>
  );
}

/**
 * The window a name was seen in, as a reader would write it.
 *
 * `min(posted_at)` over rides that all have no date is NULL, so a name whose
 * rides were never posted arrives here as two nulls. The screen used to render
 * them with `String(firstSeen)`, which put the literal word **null** in front
 * of an owner — twice, with an en dash between.
 *
 * An em dash for "no dates", like `Stat`: the absence of a window is not a
 * window of nothing. One date is printed alone rather than as a half-range,
 * and a single day is printed once rather than repeated either side of a dash,
 * because "2026-09-28 – 2026-09-28" is a range only in the sense that a point
 * is a very short line.
 */
export function seenRange(firstSeen: string | null, lastSeen: string | null): string {
  const day = (v: string | null): string | null => {
    if (typeof v !== 'string') return null;
    const d = v.trim().slice(0, 10);
    return d.length === 10 ? d : null;
  };

  const from = day(firstSeen);
  const to = day(lastSeen);

  if (from === null && to === null) return '—';
  if (from === null) return to!;
  if (to === null) return from;
  return from === to ? from : `${from} – ${to}`;
}
